import React, { useEffect } from 'react';
import {
  HttpError,
  IResourceComponentsProps,
  useNavigation,
  useNotification,
  useTranslate,
} from '@pankod/refine-core';
import {
  Backdrop,
  Box,
  CircularProgress,
  Edit,
  FormControl,
  FormHelperText,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@pankod/refine-mui';
import { useStepsForm } from '@pankod/refine-react-hook-form';
import { ADMIN_USERS_RESOURCE } from 'services/adminUserService';

export const ManagerEdit: React.FC<IResourceComponentsProps> = () => {
  const t = useTranslate();
  const { push } = useNavigation();
  const notification = useNotification();

  const {
    refineCore: { onFinish, formLoading, queryResult },
    register,
    setValue,
    getValues,
    formState: { errors },
  } = useStepsForm<any, HttpError, any>({
    stepsProps: {
      isBackValidate: false,
    },
    warnWhenUnsavedChanges: true,
    refineCoreProps: {
      redirect: false,
    },
  });

  useEffect(() => {
    const record = queryResult?.data as any;
    if (record && typeof record.offer_limit !== 'undefined') {
      setValue('offer_limit', Number(record.offer_limit));
    }
  }, [queryResult?.data, setValue]);

  const handleSubmit = async () => {
    const offerLimit = Number(getValues('offer_limit'));

    if (Number.isNaN(offerLimit) || offerLimit < 0) {
      notification.open?.({
        type: 'error',
        message: 'Offer limit must be greater than or equal to 0',
      });
      return;
    }

    try {
      await onFinish({
        offer_limit: offerLimit,
      });

      notification.open?.({
        type: 'success',
        message: 'Admin user updated successfully',
      });
      push('/admin_users');
    } catch (error) {
      notification.open?.({
        type: 'error',
        message:
          (error as Error)?.message || 'Failed to update admin user',
      });
    }
  };

  return (
    <Edit
      resource={ADMIN_USERS_RESOURCE}
      title={<Typography variant="h5">Edit Admin User</Typography>}
      isLoading={formLoading}
      saveButtonProps={{ onClick: handleSubmit }}
    >
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
        autoComplete="off"
      >
        <br />
        <Grid
          container
          sx={{
            marginX: { xs: '0px' },
          }}
        >
          <Grid item xs={12}>
            <Grid container>
              <Grid item paddingX={4} xs={12}>
                <Stack gap="24px">
                  <FormControl>
                    <TextField
                      {...register('offer_limit', {
                        required: t('errors.required.field', {
                          field: 'offer_limit',
                        }),
                        valueAsNumber: true,
                        min: {
                          value: 0,
                          message:
                            'Offer limit must be greater than or equal to 0',
                        },
                      })}
                      size="medium"
                      variant="outlined"
                      required
                      type="number"
                      label="Offer Limit (required)"
                    />
                    {errors.offer_limit && (
                      <FormHelperText error>
                        {String(errors.offer_limit.message || '')}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Backdrop
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={formLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
    </Edit>
  );
};
